import {
  Body,
  Box,
  Dialog,
  Divider,
  Form,
  FormRow,
  FormSection,
  Space,
} from "@dbn/omnichannel-design-system-g4p";
import { option } from "fp-ts";
import { constNull, pipe } from "fp-ts/function";
import {
  RuntimeLocaleKey,
  useFormatMessage,
  useFormatMoneyAmountValue,
  useFormatPercentageLike,
} from "../../intl";
import { GenericLoanResponseOutput } from "../api";
import { useAppContext } from "../../useAppContext";
import { unsafeNonNegativeInteger } from "../../globalDomain";
import { ReadOnlyFieldToValue } from "../../Common/ReadOnlyField/ReadOnlyFieldToValue";

type Props = {
  offer: GenericLoanResponseOutput;
  onDismiss: () => unknown;
};

export function OfferReviewDetailDialog(props: Props) {
  const {
    config: { enableBankFee50FE: enableBankFee50 },
  } = useAppContext();

  const formatMessage = useFormatMessage();
  const formatMoneyAmount = useFormatMoneyAmountValue();
  const formatPercentage = useFormatPercentageLike();
  const formatMoneyAmountValueInteger = useFormatMoneyAmountValue(
    unsafeNonNegativeInteger(0)
  );
  const cpiMonthlyAmount = pipe(
    props.offer.monthlyInsuranceAmount,
    option.getOrElse(() => 0)
  );

  return (
    <Dialog
      ariaLabelMessageForCloseIcon={formatMessage(
        "Modal.closeButton" as RuntimeLocaleKey
      )}
      variant="left"
      size="small"
      title={formatMessage(
        "StandardLoan.OfferApproved.OfferReview.Detail.title"
      )}
      onDismiss={option.some(props.onDismiss)}
      actions={[]}
      mobileModalHeight={"90dvh"}
    >
      <Box column grow shrink>
        <Form>
          <FormSection>
            <FormRow type="1-1">
              <ReadOnlyFieldToValue
                size="small"
                label={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.Detail.totalLoanAmount"
                )}
                value={formatMoneyAmount({
                  amount: props.offer.totalAmount,
                  currency: props.offer.currency,
                })}
                labelWeight={"bold"}
              />
              <ReadOnlyFieldToValue
                size="small"
                label={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.Detail.loanDuration"
                )}
                value={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.Detail.loanDurationValue",
                  {
                    months: props.offer.tenor,
                  }
                )}
                labelWeight={"bold"}
              />
            </FormRow>
            <FormRow type="1-1">
              <ReadOnlyFieldToValue
                size="small"
                label={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.Detail.monthlyInstallment"
                )}
                value={formatMoneyAmount({
                  amount: props.offer.installment,
                  currency: props.offer.currency,
                })}
                labelWeight={"bold"}
              />
              <ReadOnlyFieldToValue
                size="small"
                label={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.Detail.apr"
                )}
                value={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.Detail.aprValue",
                  {
                    apr: formatPercentage(props.offer.apr),
                  }
                )}
                labelWeight={"bold"}
              />
            </FormRow>
            <FormRow type="1-1">
              <ReadOnlyFieldToValue
                size="small"
                label={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.Detail.interestRate"
                )}
                value={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.Detail.interestRateValue",
                  {
                    rate: formatPercentage(props.offer.interestRate), // @TODO: check if formatting is correct
                  }
                )}
                labelWeight={"bold"}
              />
              {pipe(
                props.offer.totalPayableAmount,
                option.fold(constNull, totalPayableAmount => (
                  <ReadOnlyFieldToValue
                    size="small"
                    label={formatMessage(
                      "StandardLoan.OfferApproved.OfferReview.Detail.totalToPay"
                    )}
                    value={formatMoneyAmount({
                      amount: totalPayableAmount,
                      currency: props.offer.currency,
                    })}
                    labelWeight={"bold"}
                  />
                ))
              )}
            </FormRow>
            <FormRow type="1-1">
              <>
                {cpiMonthlyAmount > 0 && (
                  <ReadOnlyFieldToValue
                    size="small"
                    label={formatMessage(
                      "StandardLoan.OfferApproved.OfferReview.Detail.cpiMonthlyAmount"
                    )}
                    value={formatMoneyAmount({
                      amount: cpiMonthlyAmount,
                      currency: props.offer.currency,
                    })}
                    labelWeight={"bold"}
                  />
                )}
              </>
              <>
                {enableBankFee50 &&
                  pipe(
                    props.offer.bankingFee,
                    option.fold(constNull, bankingFee =>
                      pipe(
                        props.offer.bankingFeeIncluded,
                        option.fold(
                          () => (
                            <ReadOnlyFieldToValue
                              size="medium"
                              label={formatMessage(
                                "StandardLoan.CustomerOffer.detailsModal.bankingFees.label"
                              )}
                              value={formatMoneyAmountValueInteger({
                                amount: bankingFee,
                                currency: props.offer.currency,
                              })}
                              labelWeight={"bold"}
                            />
                          ),
                          feeIncluded => (
                            <ReadOnlyFieldToValue
                              size="medium"
                              label={formatMessage(
                                feeIncluded
                                  ? "StandardLoan.CustomerOffer.detailsModal.bankingFees.included.label"
                                  : "StandardLoan.CustomerOffer.detailsModal.bankingFees.notIncluded.label"
                              )}
                              value={formatMoneyAmountValueInteger({
                                amount: bankingFee,
                                currency: props.offer.currency,
                              })}
                              labelWeight={"bold"}
                            />
                          )
                        )
                      )
                    )
                  )}
              </>
            </FormRow>
          </FormSection>
        </Form>
        <Space units={6} />
        <Divider />
        <Space units={6} />
        <Body size="small" weight="regular">
          {formatMessage(
            "StandardLoan.OfferApproved.OfferReview.Detail.description"
          )}
        </Body>
      </Box>
    </Dialog>
  );
}
